const WHITE_BOUNDARY_STROKE = "#ffffff";

export const mapSettingsKey = userId => {
  if (!userId) return null;
  return `map-settings-${userId}`;
};

export const defaultMapSettings = {
  whiteBoundary: false,
};

export const getMapSettings = userId => {
  const key = mapSettingsKey(userId);
  if (!key) return { ...defaultMapSettings };

  try {
    const storedSettings = window.localStorage.getItem(key);
    if (storedSettings) {
      try {
        return { ...defaultMapSettings, ...JSON.parse(storedSettings) };
      } catch (parseError) {
        window.localStorage.setItem(key, JSON.stringify(defaultMapSettings));
      }
    } else {
      window.localStorage.setItem(key, JSON.stringify(defaultMapSettings));
    }
  } catch (error) {
    console.error("Unable to read map settings", error);
  }

  return { ...defaultMapSettings };
};

export const saveMapSettings = (userId, settings) => {
  const key = mapSettingsKey(userId);
  const nextSettings = { ...defaultMapSettings, ...settings };

  if (!key) return nextSettings;

  try {
    window.localStorage.setItem(key, JSON.stringify(nextSettings));
  } catch (error) {
    console.error("Unable to save map settings", error);
  }

  return nextSettings;
};

const strokedLayerStyle = options => {
  return options?.whiteBoundary ? { strokeColor: WHITE_BOUNDARY_STROKE } : {};
};

const layerStyleFor = (name, feature, options) => {
  return LAYERS_INFO[name]?.layerStyle(feature, options);
};

export let LAYERS_INFO = {
  "Response Zones": {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Emergency_ESZ_poly_SP_v1/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [
      { property: "TOWNNAME", label: "" },
      { property: "LAW_DisplayName", label: "LAW: " },
      { property: "FIRE_DisplayName", label: "FIRE: " },
      { property: "EMS_DisplayName", label: "EMS: " },
      { property: "PSAP_DisplayName", label: "PSAP: " },
    ],
    layerStyle: (feature, options) => {
      return {
        fillColor: "none",
        fillOpacity: 0,
        strokeWeight: 2,
        ...strokedLayerStyle(options),
      };
    },
    interactionType: "mouseover",
    updates: false,
    infoWindowStyle: "display: flex; justify-content: center",
  },
  "Interstate Markers": {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Emergency_LANDMARKS_point_SP_v1/FeatureServer/0/query?where=LANDMARKTYPE%20%3D%20'M06'&outFields=*&outSR=4326&f=geojson`,
    featureArr: [
      { property: "NAME", label: "Marker: " },
      { property: "TOWNNAME", label: "Town: " },
      { property: "COUNTY", label: "County: " },
      { property: "PRIMARYNAME", label: "Name: " },
    ],
    layerStyle: () => {
      return {
        icon: "/highway.png",
      };
    },
    interactionType: "click",
    updates: false,
  },
  Electric: {
    url: () =>
      `https://maps.vcgi.vermont.gov/arcgis/rest/services/PSD_services/OPENDATA_PSD_LAYERS_SP_NOCACHE_v1/MapServer/40/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [{ property: "COMPANYNAM", label: "Company: " }],
    layerStyle: feature => {
      console.log(feature);
      return {};
    },
    interactionType: "click",
    updates: false,
  },
  Parcels: {
    url: (north, east, south, west) => {
      return `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Cadastral_VTPARCELS_poly_standardized_parcels_SP_v1/FeatureServer/0/query?where=1%3D1&outFields=*&geometry=${west}%2C${south}%2C${east}%2C${north}&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326&f=geojson`;
    },
    featureArr: [
      { property: "E911ADDR", label: "Address: " },
      { property: "TNAME", label: "City: " },
      { property: "CAT", label: "Category: " },
      { property: "OWNER1", label: "Owner: " },
      { property: "OWNER2", label: "Owner: " },
    ],
    layerStyle: (feature, options) => {
      if (feature?.getProperty("PROPTYPE") === "ROW_ROAD") {
        return {
          fillOpacity: 0.1,
          strokeWeight: 2,
          zIndex: -1,
          ...strokedLayerStyle(options),
        };
      } else {
        return {
          fillOpacity: 0.1,
          strokeWeight: 2,
          zIndex: 1,
          ...strokedLayerStyle(options),
        };
      }
    },
    interactionType: "click",
    updates: true,
    maxZoomLevel: 10,
  },
  Hydrants: {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Emergency_HYDRANTS_point_SP_v1/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [
      { property: "HYDRANTNUMBER", label: "Hydrant #: " },
      { property: "HYDRANTTYPE", label: "Type: " },
      { property: "PRIMARYADDRESS", label: "Address: " },
      { property: "COMMENTS", label: "Comments: " },
    ],
    layerStyle: () => {
      return {
        icon: "/fire-hydrant-2.png",
      };
    },
    interactionType: "click",
    updates: false,
  },
  Landmarks: {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Emergency_LANDMARKS_point_SP_v1/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [
      { property: "NAME", label: "" },
      { property: "TOWNNAME", label: "" },
      { property: "STATE", label: "" },
      { property: "PRIMARYNAME", label: "" },
      { property: "LANDMARKTYPE", label: "" },
      { property: "COMMENTS", label: "" },
    ],
    layerStyle: () => {
      return {
        icon: "/landmark.png",
      };
    },
    interactionType: "click",
    updates: false,
  },
  Trails: {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Emergency_TRAILS_line_SP_v1/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [
      { property: "TRAILNAME", label: "" },
      { property: "TOWNNAME", label: "" },
      { property: "STATE", label: "" },
      { property: "TRAILTYPE", label: "" },
    ],
    layerStyle: (feature, options) => {
      return {
        strokeColor: options?.whiteBoundary ? WHITE_BOUNDARY_STROKE : "#39a900",
        strokeWeight: 4,
      };
    },
    interactionType: "click",
    updates: false,
  },
  Counties: {
    url: () =>
      `https://services1.arcgis.com/BkFxaEFNwHqX3tAw/arcgis/rest/services/FS_VCGI_OPENDATA_Boundary_BNDHASH_poly_counties_SP_v1/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson`,
    featureArr: [{ property: "CNTYNAME", label: "" }],
    layerStyle: (feature, options) => {
      return {
        fillColor: "none",
        fillOpacity: 0,
        strokeWeight: 2,
        ...strokedLayerStyle(options),
      };
    },
    interactionType: "mouseover",
    updates: false,
  },
};

export const infoWindowContentCreator = (
  event,
  infoWindow,
  featureArr,
  infoWindowStyle,
  map,
  name
) => {
  const feat = event.feature;
  const arr = featureArr.map((f, index) => {
    const x = feat.getProperty(f?.property);
    if (x) {
      if (index === 0) {
        return `<div style="${infoWindowStyle}"><b>${
          f?.label ? f?.label : ""
        }${x}</b></div>`;
      } else {
        return `<b>${f?.label ? f?.label : ""}</b>${x}<br/>`;
      }
    } else {
      return null;
    }
  });
  if (name === "Electric") {
    const companyName = feat.getProperty("COMPANYNAM");
    const telephone = ELECTRIC_TELEPHONE[companyName];
    if (telephone) arr.push(`<b>Contact: </b>${telephone}<br/>`);
  }
  const html = arr.join("");
  infoWindow.setContent(html);
  infoWindow.setPosition(event.latLng);
  infoWindow.setOptions({ pixelOffset: new window.google.maps.Size(0, -34) });
  infoWindow.open(map);
};

export const createDataLayer = (url, name, map, options = {}) => {
  var Layer = new window.google.maps.Data();
  Layer.loadGeoJson(url);
  Layer.setStyle(feature => {
    return layerStyleFor(name, feature, options);
  });
  const infoWindow = new window.google.maps.InfoWindow({});

  Layer.addListener(LAYERS_INFO[name]?.interactionType || "click", event =>
    infoWindowContentCreator(
      event,
      infoWindow,
      LAYERS_INFO[name]?.featureArr,
      LAYERS_INFO[name]?.infoWindowStyle,
      map,
      name
    )
  );
  if (LAYERS_INFO[name]?.updates) {
    map.addListener("idle", () => handleBoundsUpdate(map));
  }
  return Layer;
};

let fetchedLayers = {};

export const resetFetchedLayers = () => {
  fetchedLayers = {};
};

export const handleLayerToggle = (value, name, map, options = {}) => {
  if (value) {
    if (fetchedLayers[name]) {
      handleLayerVisibility(value, name, map);
    } else {
      const bounds = map?.getBounds?.()?.toJSON?.();
      if (!bounds) {
        return;
      }
      const { north, south, west, east } = bounds || {};
      const layer = createDataLayer(
        LAYERS_INFO[name]?.url(north, east, south, west),
        name,
        map,
        options
      );
      layer?.setMap(map);
      fetchedLayers = { ...fetchedLayers, ...{ [name]: layer } };
    }
  } else {
    handleLayerVisibility(value, name, map);
  }
};
const tooManyResults = new Event("exceedsTransferLimit");

export const handleBoundsUpdate = async map => {
  const bounds = map?.getBounds?.()?.toJSON?.();
  if (!bounds) {
    return;
  }
  const { north, south, west, east } = bounds || {};
  const layerNames = Object.keys(fetchedLayers);

  for (let index = 0; index < layerNames.length; index++) {
    if (LAYERS_INFO[layerNames[index]]?.updates) {
      const isMounted = !!fetchedLayers?.[layerNames[index]]?.getMap();
      if (isMounted) {
        try {
          let response = await fetch(
            LAYERS_INFO[layerNames[index]]?.url(north, east, south, west)
          );
          let featureCollection = await response.json();
          if (Object.prototype.hasOwnProperty.call(featureCollection, "properties")) {
            if (featureCollection.properties.exceededTransferLimit) {
              window.dispatchEvent(tooManyResults);
            }
          }
          fetchedLayers[layerNames[index]].addGeoJson(featureCollection);
        } catch (error) {
          console.error(
            `Unable to refresh map layer ${layerNames[index]}`,
            error
          );
        }
      }
    }
  }
};

export const handleLayerVisibility = (bool, name, map) => {
  fetchedLayers?.[name]?.setMap(bool ? map : null);
};

export const handleBoundaryStrokeToggle = (value, userId) => {
  const settings = saveMapSettings(userId, { whiteBoundary: !!value });

  Object.keys(fetchedLayers).forEach(name => {
    fetchedLayers[name]?.setStyle?.(feature =>
      layerStyleFor(name, feature, settings)
    );
  });

  return settings;
};

const ELECTRIC_TELEPHONE = {
  "Burlington Electric Department": "802-865-7364",
  "Village of Ludlow Electric Light Department": "802-228-3721",
  "Village of Swanton Electric Department": "802-868-3397",
  "Village of Barton Electric Department": "802-867-5222",
  "Village of Enosburg Falls Water & Light Dept. Inc.": "802-933-4443",
  "Hardwick Electric Department": "888-472-5201",
  "Village of Hyde Park Electric Department": "802-888-2310",
  "Village of Jacksonville Electric Company, Inc.": "802-368-7010",
  "Village of Johnson Water & LIght Department": "802-635-2611",
  "Village of Lyndonville Electric Department": "802-626-3366",
  "Morrisville Water & Light Department": "802-888-3348",
  "Town of Northfield Electric Department": "802-485-5411",
  "Village of Orleans Electric Department": "802-754-8584",
  "Stowe Electric Department": "802-253-7215",
  "Vermont Electric Co-op": "800-832-2667",
  "Washington Electric Co-op": "802-223-5245",
  "Green Mountain Power": "802-655-9217",
};
